import { Form, Select } from 'antd';
import { useTranslation } from 'react-i18next';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Store } from '../../state/store.interfaces';
import { LrvForm } from '../../common/components/LrvForm/LrvForm';
import TinyLabel from '../../common/components/TinyLabel/TinyLabel';
import { useFormErrorFields } from '../../hooks/useFormErrorFields';
import { LrvModal } from '../../common/components/LrvModal/LrvModal';
import { validateString, validateNumber } from '../../utils/validations';
import { LrvInputV2 } from '../../common/components/LrvInputV2/LrvInputV2';
import { getSamplingTypeLabel } from '../../helpers/sampling-type.helpers';
import { LrvSelectV2 } from '../../common/components/LrvSelectV2/LrvSelectV2';
import { species, unitStatuses, isOperatorRole, samplingTypes } from '../../config/commons';

import { EditAnalysis } from './interfaces';
import * as analysisSlice from './sowingSlice';
import styles from './EditAnalysisForm.module.scss';
import { getMaxSampleWeight, getMinSampleWeight } from './helpers';

interface Props {
  stockingId: string;
}

const { Option } = Select;

export const EditAnalysisForm = (props: Props) => {
  const { stockingId } = props;
  const dispatch = useDispatch();
  const [t] = useTranslation();
  const [formEditAnalysis] = Form.useForm();
  const { errorFields, resetErrorFields, extractAndSetErrorFields } = useFormErrorFields({ form: formEditAnalysis });

  const [disabledButton, setDisabledButton] = useState(false);

  const {
    sampleLimits: sampleWeightLimits,
    currentPage,
    editAnalysis,
    companySamplingTypes,
  } = useSelector((state: Store) => state.analysis);

  const { selectedStocking: stocking } = useSelector((state: Store) => state.stockings);

  const isCampusInactive = stocking.campusId.status === unitStatuses.INACTIVE;
  const maxLengthCode = 30;

  useEffect(() => {
    if (isCampusInactive) {
      setDisabledButton(true);
    }
  }, [isCampusInactive]);

  useEffect(() => {
    if (!editAnalysis.data.code) {
      return;
    }

    formEditAnalysis.setFieldsValue({
      code: editAnalysis.data.code,
      sampleWeight: editAnalysis.data.sampleWeight,
      stage: editAnalysis.data.stage,
      samplingTypeId: editAnalysis.data.samplingTypeId,
    });
  }, [editAnalysis.data.code, editAnalysis.data.sampleWeight, editAnalysis.data.stage, editAnalysis.showModal]);

  const renderLabelCampusInactive = () => {
    if (isCampusInactive) {
      return (
        <Form.Item>
          <TinyLabel className={styles.campusInactive} text={t('analysis.inactiveCampus')} />
        </Form.Item>
      );
    }

    return null;
  };

  const renderCode = () => {
    const hasError = errorFields.includes('code');

    return (
      <Form.Item
        name='code'
        rules={[() => ({ validator (rule, value) { return validateString(value); } })]}
      >
        <LrvInputV2
          theme='light'
          value={editAnalysis.data.code}
          maxLength={maxLengthCode}
          label={t('analysis.code')}
          hasError={hasError}
          onChange={(e) => {
            const editDataAnalysis: EditAnalysis = {
              ...editAnalysis.data,
              code: e.target.value,
            };

            dispatch(analysisSlice.setEditDataAnalysis(editDataAnalysis));
          }}
          disabled={isCampusInactive}
        />
      </Form.Item>
    );
  };

  const renderSampleWeight = () => {
    const hasError = errorFields.includes('sampleWeight');

    return (
      <Form.Item
        name='sampleWeight'
        rules={
          [() => ({
            validator (rule, value) {
              return validateNumber(value, false, getMinSampleWeight(sampleWeightLimits, stocking.phaseType), getMaxSampleWeight(sampleWeightLimits, stocking.phaseType), t('analysis.sampleWeightError', { min: getMinSampleWeight(sampleWeightLimits, stocking.phaseType), max: getMaxSampleWeight(sampleWeightLimits, stocking.phaseType) }));
            }
          })]
        }
      >
        <LrvInputV2
          theme='light'
          value={editAnalysis.data.sampleWeight}
          readOnly={isOperatorRole()}
          label={t('analysis.inputData.sampleWeight')}
          hasError={hasError}
          onChange={(e) => {
            const editDataAnalysis: EditAnalysis = {
              ...editAnalysis.data,
              sampleWeight: e.target.value,
            };

            dispatch(analysisSlice.setEditDataAnalysis(editDataAnalysis));
          }}
          disabled={isCampusInactive}
        />
      </Form.Item>
    );
  };

  const renderSampleTypeDropdown = () => {
    if (!companySamplingTypes.length) {
      return null;
    }

    return (
      <Form.Item
        name='samplingTypeId'
      >
        <LrvSelectV2
          theme='light'
          value={editAnalysis.data.samplingTypeId}
          onChange={(value) => {
            const editDataAnalysis: EditAnalysis = {
              ...editAnalysis.data,
              samplingTypeId: value === samplingTypes.NONE ? null : value,
            };

            dispatch(analysisSlice.setEditDataAnalysis(editDataAnalysis));
          }}
          label={t('analysis.samplingType')}
        >
          <Option key={samplingTypes.NONE} value={samplingTypes.NONE}>{getSamplingTypeLabel(samplingTypes.NONE)}</Option>
          {companySamplingTypes.map((item) => (
            <Option key={item._id} value={item._id}>{item.globalSamplingTypeId ? getSamplingTypeLabel(item.key) : item.key}</Option>
          ))}
        </LrvSelectV2>
      </Form.Item>
    );
  };

  return (
    <LrvModal
      theme='light'
      className='editAnalysisModal'
      title={t('analysis.editAnalysis')}
      open={editAnalysis.showModal}
      destroyOnClose={true}
      okButtonProps={{ id: 'submit_edit_analysis', htmlType: 'submit', form: 'formEditAnalysis', disabled: disabledButton, loading: editAnalysis.isUpdating }}
      cancelButtonProps={{ id: 'cancel_edit_analysis' }}
      onOk={() => {
        const analysisData = {
          code: editAnalysis.data.code,
          fix: true,
          inputData: {
            sampleWeight: parseFloat(editAnalysis.data.sampleWeight),
            stage: Number(editAnalysis.data.stage),
            species: species.LITOPENAEUS_VANNAMEI,
            maturationId: editAnalysis.data.maturationId,
          },
          samplingTypeId: editAnalysis.data.samplingTypeId,
        };

        const props = {
          companyId: stocking.companyId,
          analysisId: editAnalysis.data.analysisId,
          stockingId,
          analysisData,
          currentPage,
          onSuccess: () => {
            dispatch(analysisSlice.setShowModalEdit(false));
          },
        };

        dispatch(analysisSlice.updateAnalysis(props));
      }}
      okText={t('analysis.edit').toUpperCase()}
      cancelText={t('analysis.cancel').toUpperCase()}
      onCancel={() => {
        formEditAnalysis.resetFields();
        resetErrorFields();
        dispatch(analysisSlice.setShowModalEdit(false));
      }}
    >
      <LrvForm
        theme='light'
        form={formEditAnalysis}
        name='formEditAnalysis'
        id='formEditAnalysis'
        layout='vertical'
        onFieldsChange={() => {
          const disabled = formEditAnalysis.getFieldsError().filter(({ errors }) => errors.length).length > 0 || isCampusInactive;
          setDisabledButton(disabled);
          extractAndSetErrorFields();
        }}
      >
        {renderLabelCampusInactive()}
        {renderCode()}
        {renderSampleWeight()}
        {renderSampleTypeDropdown()}
      </LrvForm>
    </LrvModal>
  );
};

